import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { TITLE_BLOCK_TYPES } from "./previewTitleBlocks";

export const TitleBlockContext = createContext(null);

const TYPOGRAPHY_BY_TYPE = {
  full_title: { mt: 2, mb: 1, align: "center", variant: "h6" },
  full_subTitle: { mt: 1.5, mb: 0, align: "center", variant: "subtitle1" },
};

export const PreviewTitleBlock = memo(({ field }) => {
  const actions = useContext(TitleBlockContext);
  const type = field.type === "full_subTitle" ? "full_subTitle" : "full_title";
  const otherType = type === "full_title" ? "full_subTitle" : "full_title";
  const label = String(field.field_label ?? "");
  const instanceId = field.field_instance_id;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const finishedRef = useRef(false);

  const startEditing = useCallback(() => {
    finishedRef.current = false;
    setDraft(label);
    setEditing(true);
  }, [label]);

  useEffect(() => {
    if (actions?.autoEditId && actions.autoEditId === instanceId) {
      startEditing();
      actions.onAutoEditDone?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions?.autoEditId, instanceId]);

  const finish = (save) => {
    if (finishedRef.current) return;
    finishedRef.current = true;

    const next = draft.trim();
    setEditing(false);

    if (!save || !next) {
      // A brand-new title that was never named is discarded.
      if (!label) actions?.onDelete?.(field);
      return;
    }

    if (next !== label) {
      actions?.onChange?.(field, { field_label: next, label: next });
    }
  };

  if (editing) {
    return (
      <Box data-preview-label-editor="true" sx={{ py: 1 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          variant="filled"
          label={`${TITLE_BLOCK_TYPES[type].label} text`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={(event) => event.target.select()}
          onBlur={() => finish(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              finish(true);
            } else if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              finish(false);
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      data-preview-label-editor="true"
      onDoubleClick={startEditing}
      sx={{
        position: "relative",
        borderRadius: 1,
        px: 5,
        transition: "background-color .15s",
        "& .title-block-actions": {
          opacity: 1,
          transition: "opacity .15s",
          "@media (hover: hover)": { opacity: 0 },
        },
        "&:hover, &:focus-within": { backgroundColor: "action.hover" },
        "&:hover .title-block-actions, &:focus-within .title-block-actions": {
          opacity: 1,
        },
      }}
    >
      <Typography
        {...TYPOGRAPHY_BY_TYPE[type]}
        color={label ? "text.primary" : "text.disabled"}
        sx={{
          overflowWrap: "anywhere",
          fontStyle: label ? "normal" : "italic",
        }}
      >
        {label || "Untitled"}
      </Typography>

      <Box
        className="title-block-actions"
        sx={{
          position: "absolute",
          right: 4,
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          gap: 0.25,
        }}
      >
        <Tooltip title="Edit text">
          <IconButton
            size="small"
            aria-label="Edit title text"
            onClick={startEditing}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={`Convert to ${TITLE_BLOCK_TYPES[otherType].label.toLowerCase()}`}
        >
          <IconButton
            size="small"
            aria-label={`Convert to ${TITLE_BLOCK_TYPES[otherType].label.toLowerCase()}`}
            onClick={() => actions?.onChangeType?.(field, otherType)}
          >
            <SwapVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            aria-label="Delete title"
            onClick={() => actions?.onDelete?.(field)}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

const chipSx = {
  minWidth: 0,
  py: 0,
  px: 1,
  fontSize: 11,
  lineHeight: 1.8,
  textTransform: "none",
  borderRadius: 10,
  backgroundColor: "background.paper",
  border: "1px solid",
  borderColor: "primary.light",
};

// Zero-layout-impact hover zone. Lives inside a `position: relative` grid item.
export const InsertTitleDivider = ({ onInsert, placement = "before" }) => (
  <Box
    role="group"
    aria-label="Insert title or subtitle here"
    data-preview-label-editor="true"
    sx={{
      position: "absolute",
      left: 0,
      right: 0,
      height: 16,
      zIndex: 2,
      [placement === "after" ? "bottom" : "top"]: -8,
      display: "flex",
      alignItems: "center",
      gap: 1,
      opacity: 0,
      transition: "opacity .12s",
      "&:hover, &:focus-within": { opacity: 1 },
    }}
  >
    <Box
      sx={{ flex: 1, borderTop: "1px dashed", borderColor: "primary.light" }}
    />
    <Button
      size="small"
      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
      onClick={() => onInsert("full_title")}
      sx={chipSx}
    >
      Title
    </Button>
    <Button
      size="small"
      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
      onClick={() => onInsert("full_subTitle")}
      sx={chipSx}
    >
      Subtitle
    </Button>
    <Box
      sx={{ flex: 1, borderTop: "1px dashed", borderColor: "primary.light" }}
    />
  </Box>
);
